import React, { useEffect, useMemo, useState } from "react";

interface LogoWordmarkLoaderProps {
  visible?: boolean;
}

const firstLine = ["В", "С", "И", "Ч", "К", "И"];
const accent = "%";
const secondLine = ["Б", "Р", "О", "Ш", "У", "Р", "И"];

const LogoWordmarkLoader: React.FC<LogoWordmarkLoaderProps> = ({ visible = true }) => {
  const [activeIndex, setActiveIndex] = useState(0);

  const letters = useMemo(
    () => [
      ...firstLine.map((char) => ({ text: char, row: 0 })),
      { text: accent, row: 0 },
      ...secondLine.map((char) => ({ text: char, row: 1 })),
    ],
    [],
  );

  useEffect(() => {
    if (!visible) return;

    const interval = window.setInterval(() => {
      setActiveIndex((prev) => (prev + 1) % letters.length);
    }, 180);

    return () => window.clearInterval(interval);
  }, [letters.length, visible]);

  if (!visible) return null;

  return (
    <div style={styles.overlay}>
      <div style={styles.loaderBox}>
        <div style={styles.spinnerWrap}>
          <div style={styles.spinner} />
        </div>
        <div style={styles.logoText}>
          <div style={styles.line}>
            {firstLine.map((char, index) => {
              const phase = (index - activeIndex + letters.length) % letters.length;
              const opacity = phase === 0 ? 1 : phase === 1 ? 0.55 : 0.18;

              return (
                <span
                  key={`first-${char}-${index}`}
                  style={{
                    ...styles.letter,
                    opacity,
                    transform: phase === 0 ? "translateY(0)" : "translateY(2px)",
                  }}
                >
                  {char}
                </span>
              );
            })}
            <span
              style={{
                ...styles.letter,
                color: "#72cecb",
                opacity: (activeIndex + firstLine.length + 1) % 2 === 0 ? 1 : 0.18,
                transform: (activeIndex + firstLine.length + 1) % 2 === 0 ? "translateY(0)" : "translateY(2px)",
              }}
            >
              {accent}
            </span>
          </div>

          <div style={styles.line}>
            {secondLine.map((char, index) => {
              const phase = (index + firstLine.length + 1 - activeIndex + letters.length) % letters.length;
              const opacity = phase === 0 ? 1 : phase === 1 ? 0.55 : 0.18;

              return (
                <span
                  key={`second-${char}-${index}`}
                  style={{
                    ...styles.letter,
                    opacity,
                    transform: phase === 0 ? "translateY(0)" : "translateY(2px)",
                  }}
                >
                  {char}
                </span>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  overlay: {
    position: "fixed",
    inset: 0,
    zIndex: 9999,
    background: "transparent",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "16px",
  },
  loaderBox: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    width: "100%",
    maxWidth: 320,
    padding: 0,
    borderRadius: 0,
    background: "transparent",
    boxShadow: "none",
    backdropFilter: "none",
    border: "none",
    gap: 14,
  },
  spinnerWrap: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  spinner: {
    width: 46,
    height: 46,
    border: "4px solid rgba(47, 52, 58, 0.12)",
    borderTop: "4px solid #72cecb",
    borderRadius: "50%",
    animation: "spin 1s linear infinite",
  },
  logoText: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    fontSize: 24,
    fontWeight: 800,
    lineHeight: 1.08,
    color: "#2f343a",
    fontFamily: "Jost, sans-serif",
    letterSpacing: "0.02em",
    textTransform: "uppercase",
  },
  line: {
    display: "flex",
    alignItems: "center",
    flexWrap: "wrap",
  },
  letter: {
    display: "inline-block",
    transition: "opacity 0.18s ease, transform 0.18s ease",
  },
};

export default LogoWordmarkLoader;
